'use client'
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import PickupForm from "./partner/PickupForm";
import PostOfficeForm from "./partner/PostOfficeForm";
import addressStore from "../../store/addressStore";
import {isEmpty} from "lodash";
import appStore from "../../store/appStore";
import {fetchAddressById, fetchAddresses} from "../../services/addressServices";
import {clsx} from "clsx";
import {fetchPickupTime} from "../../services/deliveryServices";
import {text} from "../../constants/text";
import {scrollToTop} from "../../utils";
import {fetchOrderCountStatusAllTime, fetchOrderDetail} from "../../services/orderServices";
import {useOrdersStore} from "../../store/orderStore";
import {setConfirmOrder} from "../../network/orderApi";
import {isMobile} from "react-device-detect";
import {STATUS} from "../../constants";
import popupStore from "../../store/popupStore";
import Loading from "../Common/Loading/Loading";
import {toast} from "react-toastify";
// Main Component
const FutaOrderConfirmationModular = ({onClose, idAddressOrder}) => {
  const {addressAll, detailAddress, setDetailAddress} = addressStore();
  const {setLoading, isLoading} = appStore()
  const {setPopup, setPopupMobile} = popupStore()
  const {orderDetail} = useOrdersStore()
  const {logisticsProvider} = orderDetail?.shipping?.trackingInfo || {}
  const {sellerIdConfirm} = appStore();
  const {addressChoice, setAddressChoice, setAnimate, animate, setActiveTabConfirm} = appStore() || {}
  const [activeTab, setActiveTab] = useState('pickup'); // 'pickup' or 'postOffice'

  const addressPickup = useMemo(() => {
    if (!isEmpty(detailAddress)) return detailAddress
    if (!isEmpty(addressChoice)) return addressChoice
    if (isEmpty(addressAll?.items)) return null
    return addressAll?.items.find((item) => item.isPrimary)
  }, [addressAll, addressChoice, detailAddress, orderDetail?.shipping?.userAddr])
  const {name, note, phoneNumber} = addressPickup || {}
  const addressShip = useMemo(() => {
    if (!addressPickup) return '';
    const {address, commune, district, province} = addressPickup;
    return [address, commune, district, province].filter(Boolean).join(',');
  }, [addressPickup]);

  const [formData, setFormData] = useState({
    orderId: "",
    isApprove: true,
    shippingInfo: {
      sellerAddrId: '',
      shippingMethod: "standard",
      shippingData: {
        logisticsProvider: "spx"
      }
    }
  });

  const [errors, setErrors] = useState({
    pickupTimeRangeId: '',
    pickupTime: '',
  });
  const validateForm = useCallback(() => {
    const newErrors = {};
    let valid = true;
    if ((!formData.pickupTime || !formData.pickupTimeRangeId) && activeTab === 'pickup') {
      newErrors.pickupTimeRangeId = text.pickupTimeRangeRequired;
      valid = false;
    }

    if (!valid) {
      scrollToTop();
    }
    setErrors(newErrors);
    return valid;
  }, [formData, activeTab]);

  const handleTabChange = useCallback((tab) => {
    setAnimate && setAnimate({direction: 'center'})
    setActiveTabConfirm && setActiveTabConfirm(tab)
    setActiveTab(tab);
  }, [setAnimate, setActiveTabConfirm]);

  const handleClose = useCallback(() => {
    onClose && onClose()
    if (!isEmpty(addressPickup)) {
      setDetailAddress({})
      setAddressChoice(null)
    }
  }, [onClose, addressPickup, setDetailAddress, setAddressChoice]);

  const handleSelectPickupTime = useCallback((pickupTime, pickupTimeRangeId) => {
    setFormData(prev => ({
      ...prev,
      pickupTimeRangeId,
      pickupTime
    }))
  }, []);

  const handleConfirm = useCallback(() => {
    if (validateForm()) {
      setLoading(true);

      // Set a timeout to handle the case when the API call takes too long
      const timeoutId = setTimeout(() => {
        setLoading(false);
        if (isMobile) setPopupMobile({});
        else setPopup({});
        toast.error("Xác nhận đơn hàng thất bại. Vui lòng thử lại sau.");
      }, 10000);

      setConfirmOrder(formData)
        .then((res) => {
          clearTimeout(timeoutId);
          setLoading(false);

          if (res.success) {
            fetchOrderCountStatusAllTime();
            setActiveTab(STATUS.STATUS_SHIPPING_PROCESSING);
            if (isMobile) setPopupMobile({});
            else setPopup({});
          } else {
            if (isMobile) setPopupMobile({});
            else setPopup({});
            toast.error("Xác nhận đơn hàng thất bại. Vui lòng thử lại sau.");
          }
        })
        .catch((error) => {
          // Only show error toast if the timeout hasn't already triggered
          if (timeoutId) {
            clearTimeout(timeoutId);
            setLoading(false);
            if (isMobile) setPopupMobile({});
            else setPopup({});
            toast.error("Xác nhận đơn hàng thất bại. Vui lòng thử lại sau.");
          }
        });
    }
  }, [formData, validateForm, setPopup, setPopupMobile, setLoading]);

  useEffect(() => {
    if (idAddressOrder) fetchAddressById(idAddressOrder)
  }, [idAddressOrder])

  useEffect(() => {
    fetchPickupTime()
  }, [])

  // Initial data loading
  useEffect(() => {
    fetchAddresses({page: 1, size: 50})
  }, []);

  useEffect(() => {
    if (sellerIdConfirm && (
      (addressPickup?.id && logisticsProvider) ||
      activeTab === 'postOffice')
    ) {
      setFormData({
        orderId: sellerIdConfirm,
        isApprove: true,
        shippingInfo: {
          sellerAddrId: addressPickup?.id,
          shippingMethod: "standard",
          shippingData: {
            logisticsProvider,
          }
        }
      })
    }
  }, [sellerIdConfirm, addressPickup?.id, logisticsProvider])

  useEffect(() => {
    if (sellerIdConfirm) fetchOrderDetail(sellerIdConfirm)
  }, [sellerIdConfirm]);

  // Main App Logic
  return (
    <div
      className={clsx("flex items-center justify-center md:max-w-[590px] px-4 md:px-0")}>
      {isLoading && <Loading/>}
      {activeTab === 'pickup'
        ? <PickupForm
          addressPickup={addressPickup}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          onClose={handleClose}
          addressShip={addressShip}
          noted={note}
          name={name}
          phoneNumber={phoneNumber}
          animate={animate}
          errors={errors}
          handleConfirm={handleConfirm}
          handleSelectPickupTime={handleSelectPickupTime}
        />
        : <PostOfficeForm
          animate={animate}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          onClose={onClose}
          handleConfirm={handleConfirm}
        />}
    </div>
  );
};

export default FutaOrderConfirmationModular;
